import { useQuery } from '@apollo/client';
import { useResource } from '@landsbankinn/l16n';
import { LandsbankinnContext } from '@landsbankinn/utils';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import React, { useContext } from 'react';
import type {
  fetchStocksIntradayHistory,
  fetchStocksIntradayHistory_intradayHistory,
} from '../../../../graphql/__generated__/fetchStocksIntradayHistory';
import { FETCH_STOCKS_INTRADAY_HISTORY } from '../../../../graphql/fetchStocksIntradayHistory';
import { DataTable } from '../../../table/DataTable';
import {
  MarketsBodyBold,
  MarketsNumberBold,
  StickyFirstColumn,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import res from './../Stocks.res';

interface IProps {
  intraday?: fetchStocksIntradayHistory;
  prefix: string;
}

export function StocksTradesTable({ intraday, prefix }: IProps) {
  const { r, locale } = useResource();

  const { client } = useContext(LandsbankinnContext);
  const { data = intraday } = useQuery<fetchStocksIntradayHistory>(
    FETCH_STOCKS_INTRADAY_HISTORY,
    {
      client,
      variables: { first: 100 },
      pollInterval: 30000,
    },
  );

  const columns = React.useMemo(
    () => [
      {
        Header: r(res.company),
        accessor: 'symbol', // accessor is the "key" in the data
        headerStyle: {
          flexDirection: 'row-reverse',
        },
        Cell: ({ value }: any) => <MarketsBodyBold>{value}</MarketsBodyBold>,
      },
      {
        Header: r(res.price),
        accessor: 'price',
        Cell: ({ value }: any) => (
          <MarketsNumberBold
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.quantity),
        accessor: 'volume',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.value),
        accessor: 'value',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            displayISK
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
          whiteSpace: 'nowrap',
        },
      },
      {
        Header: r(res.time),
        accessor: 'date',
        Cell: ({ value }: any) =>
          isToday(new Date(value))
            ? format(new Date(value), 'HH:mm')
            : format(
                new Date(value),
                locale === 'is' ? 'dd.MM.yyyy HH:mm' : 'dd/mm/yyyy HH:mm',
              ),
        style: {
          textAlign: 'right',
        },
      },
    ],
    [],
  );

  const tableData = React.useMemo(() => {
    return data?.intradayHistory;
  }, [data]);

  return (
    <StickyFirstColumn>
      <DataTable<fetchStocksIntradayHistory_intradayHistory>
        aria-label={r(res.stocks)}
        data={tableData as any}
        columns={columns}
        colorTheme="ternary"
        initialState={{
          sortBy: [
            {
              id: 'date',
              desc: true,
            },
          ],
        }}
        rowLink={({ original: stock }: any) => ({
          href: `${prefix}/[symbol]`,
          as: `${prefix}/${stock.symbol.split(' ').join('_').toLowerCase()}`,
        })}
        isClickable
      />
    </StickyFirstColumn>
  );
}
